import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';
import AdvertiserSwitcher from './AdvertiserSwitcher';
import ClientBalanceWidget from './ClientBalanceWidget';
import SupportModal from './SupportModal';
import { getBranding, hasFeature } from '../config/branding';
import { useTranslation } from 'react-i18next';
import '../styles/reports.css';
import '../styles/users.css';
import '../styles/regional-offices.css';
import '../styles/agencies.css';
import '../styles/advertiser-switcher.css';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isAdmin = user?.role === 'admin' || user?.role === 'regional_admin';
  const isSuperAdmin = user?.role === 'admin';
  const [pendingCount, setPendingCount] = useState(0);

  const loadPendingCount = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const resp = await api.get('/api/admin/users/pending-count');
      setPendingCount(resp.data.count || 0);
    } catch {}
  }, [isAdmin]);

  useEffect(() => {
    loadPendingCount();
    const interval = setInterval(loadPendingCount, 60000); // check every minute
    return () => clearInterval(interval);
  }, [loadPendingCount]);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
  const closeSidebar = () => setSidebarOpen(false);

  // Close user menu on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  return (
    <>
      {/* Background decorative elements */}
      <div className="bg-gradient"></div>
      <div className="bg-orbs">
        <div className="orb orb-1"></div>
        <div className="orb orb-2"></div>
        <div className="orb orb-3"></div>
      </div>

      {/* Mobile header */}
      <header className="mobile-header">
        <Link to="/" className="mobile-header-logo" onClick={closeSidebar}>
          {getBranding().edition !== 'sense'
            ? <span className="brand-mark">{getBranding().brand_name.toLowerCase()}</span>
            : <img src="/favicon.svg" alt={getBranding().brand_name} className="mobile-logo-icon" />}
        </Link>
        <button className="mobile-menu-btn" onClick={toggleSidebar} aria-label={t('common.toggleMenu')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {sidebarOpen ? (
              <>
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </>
            ) : (
              <>
                <line x1="3" y1="6" x2="21" y2="6"></line>
                <line x1="3" y1="12" x2="21" y2="12"></line>
                <line x1="3" y1="18" x2="21" y2="18"></line>
              </>
            )}
          </svg>
        </button>
      </header>

      <div className="app-container">
        {/* Sidebar */}
        <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="logo">
            <Link to="/">
              {getBranding().edition !== 'sense'
                ? <span className="brand-mark">{getBranding().brand_name.toLowerCase()}</span>
                : <img src="/logo.png" alt={getBranding().brand_name} />}
            </Link>
          </div>

          {/* Client Switcher */}
          <AdvertiserSwitcher />

          <div className="sidebar-nav-scroll">
          {/* Main Section */}
          <nav className="nav-section">
            <div className="nav-label">{t('nav.sections.main')}</div>
            <Link to="/" className={`nav-item ${isActive('/') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 13h8V3H3v10zm0 8h8v-6H3v6zm10 0h8V11h-8v10zm0-18v6h8V3h-8z"/>
              </svg>
              <span>{t('nav.dashboard')}</span>
            </Link>
            <Link to="/campaigns" className={`nav-item ${isActive('/campaigns') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6zm7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z"/>
              </svg>
              <span>{t('nav.campaigns')}</span>
            </Link>
            {hasFeature('research') && (
            <Link to="/research" className={`nav-item ${isActive('/research') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
              </svg>
              <span>{t('nav.research')}</span>
            </Link>
            )}
          </nav>

          {/* Analytics Section */}
          <nav className="nav-section">
            <div className="nav-label">{t('nav.sections.analytics')}</div>
            <Link to="/reports" className={`nav-item ${isActive('/reports') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
              </svg>
              <span>{t('nav.reports')}</span>
            </Link>
            {hasFeature('conversions') && (
            <Link to="/conversions" className={`nav-item ${isActive('/conversions') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.89 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z"/>
              </svg>
              <span>{t('nav.conversions')}</span>
            </Link>
            )}
            {hasFeature('remarketing') && (
            <Link to="/remarketing-lists" className={`nav-item ${isActive('/remarketing-lists') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
              </svg>
              <span>{t('nav.remarketing')}</span>
            </Link>
            )}
          </nav>

          {/* Assets Section */}
          {hasFeature('creatives') && (
          <nav className="nav-section">
            <div className="nav-label">{t('nav.sections.assets')}</div>
            <Link to="/creatives" className={`nav-item ${isActive('/creatives') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 6H2v14c0 1.1.9 2 2 2h14v-2H4V6zm16-4H8c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm-1 9h-4v4h-2v-4H9V9h4V5h2v4h4v2z"/>
              </svg>
              <span>{t('nav.creatives')}</span>
            </Link>
          </nav>
          )}

          {/* Inventory Section */}
          <nav className="nav-section">
            <div className="nav-label">{t('nav.sections.inventory')}</div>
            <Link to="/url-lists" className={`nav-item ${isActive('/url-lists') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.9 12c0-1.71 1.39-3.1 3.1-3.1h4V7H7c-2.76 0-5 2.24-5 5s2.24 5 5 5h4v-1.9H7c-1.71 0-3.1-1.39-3.1-3.1zM8 13h8v-2H8v2zm9-6h-4v1.9h4c1.71 0 3.1 1.39 3.1 3.1s-1.39 3.1-3.1 3.1h-4V17h4c2.76 0 5-2.24 5-5s-2.24-5-5-5z"/>
              </svg>
              <span>{t('nav.urlLists')}</span>
            </Link>
            <Link to="/keyword-lists" className={`nav-item ${isActive('/keyword-lists') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
                <path d="M7 9h5v1H7z"/>
              </svg>
              <span>{t('nav.keywordLists')}</span>
            </Link>
            <Link to="/targeting-templates" className={`nav-item ${isActive('/targeting-templates') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3 3h8v8H3V3zm10 0h8v8h-8V3zM3 13h8v8H3v-8zm10 0h8v8h-8v-8z"/>
              </svg>
              <span>{t('nav.targetingTemplates')}</span>
            </Link>
            <Link to="/super-geo" className={`nav-item ${isActive('/super-geo') ? 'active' : ''}`} onClick={closeSidebar}>
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
              </svg>
              <span>{t('nav.superGeoLists')}</span>
            </Link>
          </nav>

          {/* Finance Section - for admins and managers */}
          {hasFeature('billing') && (isSuperAdmin || isAdmin || user?.role === 'manager') && (
            <nav className="nav-section">
              <div className="nav-label">{t('nav.sections.finance')}</div>
              <Link to="/billing" className={`nav-item ${isActive('/billing') ? 'active' : ''}`} onClick={closeSidebar}>
                <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                </svg>
                <span>{t('nav.billing')}</span>
              </Link>
              <Link to="/transactions" className={`nav-item ${isActive('/transactions') ? 'active' : ''}`} onClick={closeSidebar}>
                <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 14V6c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zm-2 0H3V6h14v8zm-7-7c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm13 0v11c0 1.1-.9 2-2 2H4v-2h17V7h2z"/>
                </svg>
                <span>{t('nav.transactions')}</span>
              </Link>
              <Link to="/certificates" className={`nav-item ${isActive('/certificates') ? 'active' : ''}`} onClick={closeSidebar}>
                <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                </svg>
                <span>{t('nav.certificates')}</span>
              </Link>
            </nav>
          )}

          {/* Finance section for external users.
              The role 'client' = a client OF an agency. They never see Finance —
              the agency handles their money. Other external roles (freelancer
              who manages their own accounts, smb_client running self-service)
              do see it. */}
          {(() => {
            const role = user?.role;
            const externalSelfManaged = ['agency_admin', 'agency_manager', 'agency_buyer',
                                          'freelancer', 'smb_client'];
            const showFinanceSection =
              hasFeature('billing')
              && !isSuperAdmin && !isAdmin && role !== 'manager'
              && externalSelfManaged.includes(role || '');
            if (!showFinanceSection) return null;
            return (
              <nav className="nav-section">
                <div className="nav-label">{t('nav.sections.finance')}</div>
                <Link to="/billing" className={`nav-item ${isActive('/billing') ? 'active' : ''}`} onClick={closeSidebar}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
                  </svg>
                  <span>{t('nav.billing')}</span>
                </Link>
                <Link to="/transactions" className={`nav-item ${isActive('/transactions') ? 'active' : ''}`} onClick={closeSidebar}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 14V6c0-1.1-.9-2-2-2H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zm-2 0H3V6h14v8zm-7-7c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3zm13 0v11c0 1.1-.9 2-2 2H4v-2h17V7h2z"/>
                  </svg>
                  <span>{t('nav.transactions')}</span>
                </Link>
                <Link to="/certificates" className={`nav-item ${isActive('/certificates') ? 'active' : ''}`} onClick={closeSidebar}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm-2 16l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                  </svg>
                  <span>{t('nav.certificates')}</span>
                </Link>
              </nav>
            );
          })()}

          {/* Admin Section - only for admins, managers, and agency_admin */}
          {(isAdmin || user?.role === 'manager' || user?.role === 'agency_admin' || user?.role === 'agency_manager' || user?.role === 'freelancer') && (
            <nav className="nav-section">
              <div className="nav-label">{t('nav.sections.admin')}</div>
              {(isAdmin || user?.role === 'agency_admin') && (
                <Link to="/users" className={`nav-item ${isActive('/users') ? 'active' : ''}`} onClick={() => { closeSidebar(); setPendingCount(0); }}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-.97.05 1.16.84 1.97 1.97 1.97 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z"/>
                  </svg>
                  <span>{t('nav.users')}</span>
                  {pendingCount > 0 && (
                    <span style={{
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      background: '#e53935',
                      color: '#fff',
                      fontSize: 10,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                      marginLeft: 6,
                    }}>
                      {pendingCount}
                    </span>
                  )}
                </Link>
              )}
              <Link to="/clients" className={`nav-item ${isActive('/clients') ? 'active' : ''}`} onClick={closeSidebar}>
                <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 7V3H2v18h20V7H12zM6 19H4v-2h2v2zm0-4H4v-2h2v2zm0-4H4V9h2v2zm0-4H4V5h2v2zm4 12H8v-2h2v2zm0-4H8v-2h2v2zm0-4H8V9h2v2zm0-4H8V5h2v2zm10 12h-8v-2h2v-2h-2v-2h2v-2h-2V9h8v10zm-2-8h-2v2h2v-2zm0 4h-2v2h2v-2z"/>
                </svg>
                <span>{t('nav.clients')}</span>
              </Link>
              {isAdmin && (
                <Link to="/agencies" className={`nav-item ${isActive('/agencies') ? 'active' : ''}`} onClick={closeSidebar}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 7h-4V5c0-1.1-.9-2-2-2h-4c-1.1 0-2 .9-2 2v2H4c-1.1 0-2 .9-2 2v11h20V9c0-1.1-.9-2-2-2zM10 5h4v2h-4V5z"/>
                  </svg>
                  <span>{t('nav.agencies')}</span>
                </Link>
              )}
              {isSuperAdmin && (
                <Link to="/regional-offices" className={`nav-item ${isActive('/regional-offices') ? 'active' : ''}`} onClick={closeSidebar}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
                  </svg>
                  <span>{t('nav.regionalOffices')}</span>
                </Link>
              )}
              {hasFeature('payments') && isSuperAdmin && (
                <Link to="/admin/payment-profiles" className={`nav-item ${isActive('/admin/payment-profiles') ? 'active' : ''}`} onClick={closeSidebar}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 4H4c-1.11 0-1.99.89-1.99 2L2 18c0 1.11.89 2 2 2h16c1.11 0 2-.89 2-2V6c0-1.11-.89-2-2-2zm0 14H4v-6h16v6zm0-10H4V6h16v2z"/>
                  </svg>
                  <span>{t('nav.paymentProfiles')}</span>
                </Link>
              )}
              {hasFeature('discounts') && isSuperAdmin && (
                <Link to="/discounts" className={`nav-item ${isActive('/discounts') ? 'active' : ''}`} onClick={closeSidebar}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.5 3.5L20.5 5.5L5.5 20.5L3.5 18.5L18.5 3.5Z"/>
                    <circle cx="7" cy="7" r="3"/>
                    <circle cx="17" cy="17" r="3"/>
                  </svg>
                  <span>{t('nav.discounts')}</span>
                </Link>
              )}
              {isSuperAdmin && (
                <Link to="/commissions" className={`nav-item ${isActive('/commissions') ? 'active' : ''}`} onClick={closeSidebar}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="12" y1="1" x2="12" y2="23"/>
                    <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                  </svg>
                  <span>{t('nav.commissions')}</span>
                </Link>
              )}
              {isSuperAdmin && (
                <Link to="/audit-log" className={`nav-item ${isActive('/audit-log') ? 'active' : ''}`} onClick={closeSidebar}>
                  <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 3h-4.18C14.4 1.84 13.3 1 12 1c-1.3 0-2.4.84-2.82 2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-7 0c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zm-2 14l-4-4 1.41-1.41L10 14.17l6.59-6.59L18 9l-8 8z"/>
                  </svg>
                  <span>{t('nav.auditLog')}</span>
                </Link>
              )}
            </nav>
          )}

          {/* Help / Support — visible to everyone */}
          <nav className="nav-section">
            <div className="nav-label">{t('nav.sections.support')}</div>
            <button
              type="button"
              className="nav-item"
              onClick={() => { setSupportModalOpen(true); closeSidebar(); }}
              style={{ background: 'none', border: 'none', width: '100%', textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit' }}
            >
              <svg className="nav-item-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
              </svg>
              <span>{t('nav.help')}</span>
            </button>
          </nav>
          </div>

          {/* User menu at bottom */}
          {user && (
            <div className="sidebar-user" ref={userMenuRef}>
              <div className="sidebar-user-info" onClick={() => setUserMenuOpen(!userMenuOpen)} style={{ cursor: 'pointer' }}>
                <div className="sidebar-user-avatar">
                  {user.first_name?.[0]}{user.last_name?.[0]}
                </div>
                <div className="sidebar-user-details">
                  <span className="sidebar-user-name">{user.first_name} {user.last_name}</span>
                  <span className="sidebar-user-email">{user.email}</span>
                </div>
              </div>
              <button
                className="sidebar-logout-btn"
                onClick={async () => {
                  await logout();
                  navigate('/login');
                }}
                title={t('common.logout')}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                  <polyline points="16 17 21 12 16 7"/>
                  <line x1="21" y1="12" x2="9" y2="12"/>
                </svg>
              </button>
              {userMenuOpen && (
                <div className="sidebar-user-dropdown">
                  <button
                    className="sidebar-user-dropdown-item"
                    onClick={() => {
                      setUserMenuOpen(false);
                      closeSidebar();
                      navigate('/settings');
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="3"/>
                      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
                    </svg>
                    {t('common.settings')}
                  </button>
                  <button
                    className="sidebar-user-dropdown-item sidebar-user-dropdown-logout"
                    onClick={async () => {
                      setUserMenuOpen(false);
                      await logout();
                      navigate('/login');
                    }}
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                      <polyline points="16 17 21 12 16 7"/>
                      <line x1="21" y1="12" x2="9" y2="12"/>
                    </svg>
                    {t('common.logout')}
                  </button>
                </div>
              )}
            </div>
          )}

        </aside>

        {/* Sidebar overlay - закрывает контент, но не сайдбар */}
        <div
          className={`sidebar-overlay ${sidebarOpen ? 'visible' : ''}`}
          onClick={closeSidebar}
        ></div>

        {/* Main Content */}
        <main className="main-content">
          {children}
        </main>
      </div>
      <SupportModal open={supportModalOpen} onClose={() => setSupportModalOpen(false)} />
    </>
  );
};

export default Layout;
